import {useEffect, useRef} from "react";

interface IntersectionObserverHookReturn {
    observeElement: (element: HTMLElement) => void;
    unobserveElement: (element: HTMLElement) => void;
}

type ActiveElementCallback = (data: {
    activeElementId: string | null;
    intersectingElements: Map<string, HTMLElement>;
}) => void;

/**
 * Sorting function that determines which DOM element should be the active one
 * from the current set of intersecting elements.
 */
export type SortingFunction = (
    intersectingElements: Map<string, HTMLElement>,
    nodeParentMap?: Record<string, string>,
) => string | null;

interface UseIntersectionObserverOptions {
    onActiveElementChange: ActiveElementCallback;
    sortingFunction?: SortingFunction;
    debounceMs?: number;
    nodeParentMap?: Record<string, string>;
}

/**
 * Default sorting function that returns the topmost element
 */
const defaultSortingFunction: SortingFunction = (
    intersectingElements: Map<string, HTMLElement>,
    nodeParentMap?: Record<string, string>,
) => {
    if (intersectingElements.size === 0) {
        return null;
    }

    if (intersectingElements.size === 1) {
        return Array.from(intersectingElements.keys())[0] ?? null;
    }

    // See if there are any elements that are intersecting the upper border of the container
    const intersectingElementsAtTop = Array.from(
        intersectingElements.values(),
    ).filter((element) => {
        const rect = element.getBoundingClientRect();
        return rect.top <= 0;
    });
    // If there is exactly one element that is intersecting the upper border of the container, return it as the current active element.
    if (intersectingElementsAtTop.length === 1) {
        return intersectingElementsAtTop[0].dataset.itemId ?? null;
    }

    // If there are multiple elements that are intersecting the upper border, we need to figure out the leaf node
    const atTopIds = new Set<string>();
    intersectingElementsAtTop.forEach((element) => {
        if (element.dataset.itemId) {
            atTopIds.add(element.dataset.itemId);
        }
    });
    // For each element in intersectingElementsAtTop, check if its parent also exist in atTopIds.
    intersectingElementsAtTop.forEach((element) => {
        const parentId = nodeParentMap?.[element.dataset.itemId ?? ""];
        if (parentId && atTopIds.has(parentId)) {
            atTopIds.delete(parentId);
        }
    });

    if (atTopIds.size === 1) {
        return Array.from(atTopIds.values())[0] ?? null;
    }

    // If a nodeParentMap is provided, first remove all elements that have descendants in intersectingElements.
    const filteredIntersectingElements = new Map<string, HTMLElement>(
        intersectingElements,
    );
    if (nodeParentMap) {
        for (const [id] of intersectingElements) {
            const parentId = nodeParentMap[id];
            if (parentId && filteredIntersectingElements.has(parentId)) {
                filteredIntersectingElements.delete(parentId);
            }
        }
    }

    // Find the topmost element by comparing bounding rects
    const topmostId = Array.from(filteredIntersectingElements.keys()).reduce(
        (a, b) => {
            const aRect = filteredIntersectingElements
                .get(a)
                ?.getBoundingClientRect();
            const bRect = filteredIntersectingElements
                .get(b)
                ?.getBoundingClientRect();
            return (aRect?.top ?? 0) < (bRect?.top ?? 0) ? a : b;
        },
    );

    return topmostId;
};

const isRefElement = (
    element: unknown,
): element is React.RefObject<Element | null | undefined> => {
    if (element instanceof Element) {
        return false;
    }
    return !!element && !!("current" in (element as object));
};

/**
 * Hook to observe elements intersecting with a scrollable container
 * and track which element is currently "active" based on a sorting function.
 *
 * Important! Each node that is observed must have a data-item-id attribute.
 */
export const useIntersectionObserver = (
    observerOptions: Omit<IntersectionObserverInit, "root"> & {
        root: Element | null | React.RefObject<Element | null | undefined>;
    },
    options: UseIntersectionObserverOptions,
): IntersectionObserverHookReturn => {
    const {
        onActiveElementChange,
        sortingFunction = defaultSortingFunction,
        debounceMs = 200,
        nodeParentMap,
    } = options;

    const intersectingElementNodes = useRef<Map<string, HTMLElement>>(
        new Map(),
    );
    const debouncer = useRef<NodeJS.Timeout | null>(null);
    const unobserveDebouncer = useRef<NodeJS.Timeout | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        let rootElement;
        if (isRefElement(observerOptions.root)) {
            rootElement = observerOptions.root.current;
        } else {
            rootElement = observerOptions.root;
        }
        if (!rootElement) {
            return;
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (debouncer.current) {
                    clearTimeout(debouncer.current);
                }

                entries.forEach((entry) => {
                    const itemId = (entry.target as HTMLElement).dataset.itemId;

                    if (!itemId) {
                        console.warn(
                            "Element missing data-item-id attribute",
                            entry.target,
                        );
                        return;
                    }

                    if (entry.isIntersecting) {
                        intersectingElementNodes.current.set(
                            itemId,
                            entry.target as HTMLElement,
                        );
                    } else {
                        intersectingElementNodes.current.delete(itemId);
                    }
                });

                debouncer.current = setTimeout(() => {
                    const activeElementId = sortingFunction(
                        intersectingElementNodes.current,
                        nodeParentMap,
                    );

                    onActiveElementChange({
                        activeElementId: activeElementId ?? null,
                        intersectingElements: intersectingElementNodes.current,
                    });
                }, debounceMs);
            },
            {
                rootMargin: "0px 0px 0px 0px",
                ...observerOptions,
                root: rootElement,
            },
        );

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (debouncer.current) {
                clearTimeout(debouncer.current);
            }
        };
        // Only recreate observer when root element changes.
    }, [observerOptions.root]); // eslint-disable-line react-hooks/exhaustive-deps

    const observeElement = (element: HTMLElement) => {
        if (observerRef.current) {
            if (!element.dataset.itemId) {
                throw new Error(
                    "Element must have a data-item-id attribute to be observed",
                );
            }
            observerRef.current.observe(element);
        }
    };

    const unobserveElement = (element: HTMLElement) => {
        if (observerRef.current) {
            observerRef.current.unobserve(element);
            if (
                intersectingElementNodes.current.has(
                    element.dataset.itemId ?? "",
                )
            ) {
                intersectingElementNodes.current.delete(
                    element.dataset.itemId ?? "",
                );
                unobserveDebouncer.current = setTimeout(() => {
                    if (unobserveDebouncer.current) {
                        clearTimeout(unobserveDebouncer.current);
                    }
                    const activeElementId = sortingFunction(
                        intersectingElementNodes.current,
                        nodeParentMap,
                    );
                    onActiveElementChange({
                        activeElementId: activeElementId ?? null,
                        intersectingElements: intersectingElementNodes.current,
                    });
                    unobserveDebouncer.current = null;
                }, 0);
            }
        }
    };

    return {
        observeElement,
        unobserveElement,
    };
};
